import PropTypes from "prop-types";
import React, { useCallback, useContext, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch } from "react-redux";
import { LoadingContext, M_StateActions, MTable } from "src/M_Components";

import "../config-page.css";

//region Constants

/**
 * Route used to open the Tender CRUD page.
 *
 * Important:
 * - Spot and Non-Spot tenders use the same unified Tender CRUD route;
 * - the selected Tender row and operation define how the CRUD page behaves.
 */
const TENDER_CRUD_ROUTE = "tenders/TenderConfigCrud";

/**
 * Fixed TenderTypeId used by the backend to identify Spot Tenders.
 */
const SPOT_TENDER_TYPE_ID = 1;

//endregion

//region Component

/**
 * Renders the Spot Tenders configuration tab.
 *
 * Responsibilities:
 * - render the common configuration-page layout in compact tabs mode;
 * - keep the page visually aligned with other Config tabs through config-page.css;
 * - render only Spot Tenders using the fixed TenderTypeId filter;
 * - replace default MTable View/Edit actions with tab-based navigation;
 * - persist the selected Tender before opening the unified Tender CRUD page.
 *
 * Important:
 * - this component is rendered inside a tabs page;
 * - therefore it uses the config-page--tabs modifier;
 * - the MTable keeps tabsRailWidth because this screen is constrained by the tabs rail.
 *
 * @param {object} props - Component props.
 * @param {Array<object>} props.roles - User role permissions.
 * @param {Function} props.onCreateTab - Opens a new application tab.
 * @param {Function} props.toastMessages - Shows toast notifications.
 * @returns {JSX.Element} Spot Tenders configuration tab.
 */
export default function SpotTenderConfig({
    roles,
    onCreateTab,
    toastMessages,
}) {
    const ctx = useContext(LoadingContext);
    const dispatch = useDispatch();
    const { t } = useTranslation();

    const [refresh, setRefresh] = useState(false);

    /**
     * Removes table actions that are not required in this configuration tab.
     *
     * Important:
     * - Create is disabled because this tab is only for existing Spot Tenders;
     * - Print and Copy are not part of the desired Spot Tender workflow.
     */
    const removeContextItems = useMemo(
        () => ["Print", "Copy", "Create"],
        []
    );

    /**
     * Opens the unified Tender CRUD tab for the requested Spot Tender operation.
     *
     * Flow:
     * - persists the operation in M_State;
     * - validates row selection for View/Edit/Copy;
     * - persists the selected Tender row when required;
     * - opens the unified Tender CRUD route with a contextual tab label.
     *
     * @param {"Create"|"Edit"|"Copy"|"View"} operation - Requested CRUD operation.
     * @param {object|null|undefined} row - Selected Tender row.
     * @returns {void}
     */
    const openSpotTenderCrud = useCallback(
        (operation, row) => {
            dispatch(
                M_StateActions.addM_State({
                    key: "operation",
                    value: operation,
                })
            );

            if (operation !== "Create") {
                if (!row) {
                    toastMessages("error", "", t("SelectedLine"));
                    return;
                }

                dispatch(
                    M_StateActions.addM_State({
                        key: "TenderLine",
                        value: row,
                    })
                );
            }

            const safeRequestForQuotes = row?.RequestForQuotes ?? "";

            const tabLabel =
                operation === "Create"
                    ? t("LabelNewTender")
                    : operation === "View"
                        ? `${t("LabelViewTender")} "${safeRequestForQuotes}"`
                        : `${t("LabelEditTender")} "${safeRequestForQuotes}"`;

            onCreateTab(TENDER_CRUD_ROUTE, tabLabel);
        },
        [dispatch, onCreateTab, t, toastMessages]
    );

    /**
     * Replaces MTable default CRUD handlers with the tab-based Spot Tender workflow.
     *
     * Important:
     * - Create is intentionally not exposed in this tab;
     * - View and Edit open the unified Tender CRUD route with the selected row.
     */
    const replaceFunctions = useMemo(
        () =>
            new Map([
                ["View", (row) => openSpotTenderCrud("View", row)],
                ["Edit", (row) => openSpotTenderCrud("Edit", row)],
            ]),
        [openSpotTenderCrud]
    );

    return (
        <div
            className="config-page config-page--tabs"
            style={{ "--config-page-title-color": ctx.secondaryColor }}
        >
            <div className="config-page__header">
                <div className="config-page__title">
                    {t("SpotTenders")}
                </div>

                <div className="config-page__description">
                    {t("InfoSpotTenders")}
                </div>
            </div>

            <div className="config-page__table">
                <MTable
                    filtersOnTable
                    entityName="Tenders"
                    roleName="Tenders"
                    rolePermissions={roles?.map((role) => role.Value)}
                    removeContextItems={removeContextItems}
                    refresh={refresh}
                    setRefresh={setRefresh}
                    useSessionStorage={false}
                    FILTER={`TenderTypeId eq ${SPOT_TENDER_TYPE_ID}`}
                    replaceFunctions={replaceFunctions}
                    tabsRailWidth={350}
                />
            </div>
        </div>
    );
}

//endregion

//region PropTypes

SpotTenderConfig.propTypes = {
    roles: PropTypes.array,
    onCreateTab: PropTypes.func,
    toastMessages: PropTypes.func,
};

//endregion